import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  FormHelper,
  OnyxButtonComponent,
  OnyxDatepickerComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxFormMode,
  OnyxIconComponent,
  OnyxSectionComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxToggleComponent,
  OnyxUploadComponent,
} from '@onyx/angular';
import { cloneDeep, isObject, pick } from 'lodash';
import { Duration } from 'luxon';
import { filter, map, of, timer } from 'rxjs';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../common/services/preferences.service';

type FleetInsuranceFormGroup = ReturnType<
  (typeof FleetInsuranceFormComponent)['buildForm']
>;

@Component({
  selector: 'app-fleet-insurance-form',
  imports: [
    OnyxSectionComponent,
    TranslatePipe,
    OnyxToggleComponent,
    OnyxButtonComponent,
    OnyxIconComponent,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxDatepickerComponent,
    NgTemplateOutlet,
    OnyxFormGroupComponent,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxUploadComponent,
  ],
  templateUrl: './fleet-insurance-form.component.html',
  styleUrl: './fleet-insurance-form.component.scss',
})
export class FleetInsuranceFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = input.required<FleetInsuranceFormGroup>();
  public readonly type = input.required<
    'thirdPartyLiability' | 'comprehensive'
  >();
  public readonly mode = input.required<OnyxFormMode>();
  public readonly template = input<FleetInsuranceFormGroup>();

  protected readonly I18N = 'fleet.fleetForm.insurance';

  private readonly TEMPLATE_FIELDS = [
    'insuranceNumber',
    'expiryDate',
    'contractScan',
  ] as const;

  protected currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );

  protected isOptional = computed(() => this.type() === 'comprehensive');
  protected copyTemplateDisabled!: Signal<boolean>;
  protected copyTemplateSuccess = signal(false);
  protected showForm = signal(false);

  private enabled = computed(() => this.showForm() || !this.isOptional());

  public ngOnInit() {
    const template = this.template();
    this.copyTemplateDisabled = toSignal(
      template
        ? template.valueChanges.pipe(
            map(() => pick(template.controls, this.TEMPLATE_FIELDS)),
            map((controls) =>
              Object.values(controls).some((control) => !control.valid),
            ),
          )
        : of(true),
      {
        initialValue: true,
        injector: this.injector,
      },
    );

    this.showForm.set(
      Object.values(this.form().getRawValue()).some((field) =>
        isObject(field) ? (field as any).value : field,
      ),
    );

    effect(
      () =>
        ValidationHelper.toggleControls(this.form(), {
          enabled: this.enabled(),
          reset: true,
        }),
      { injector: this.injector },
    );

    if (this.mode() === OnyxFormMode.ADD) {
      this.form()
        .get('annualCost.currency')!
        .valueChanges.pipe(
          filter(() => this.enabled()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((currency) =>
          this.form().patchValue({ insuranceSum: { currency } }),
        );
    }
  }

  protected copyTemplate(): void {
    const template = this.template();
    if (!template) return;

    const data = pick(template.getRawValue(), this.TEMPLATE_FIELDS);
    this.form().patchValue(cloneDeep(data));
    this.showForm.set(true);

    this.copyTemplateSuccess.set(true);
    timer(Duration.fromDurationLike({ seconds: 2 }).toMillis())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.copyTemplateSuccess.set(false));
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      insuranceNumber: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      expiryDate: fb.control<string | null>(null, [Validators.required]),
      annualCost: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      insuranceSum: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
        optional: true,
      }),
      contractScan: fb.control<string | null>(null),
    });
  }
}
